import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import config from "../../config.json";

import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import FeatureEffect from "@arcgis/core/layers/support/FeatureEffect";

import RefsContext from '../../contexts/RefsContext';
import { getFeatureLayers, getParametrosWidget } from '../../utils/mapaUtils';

import TextoInput from '../common/textoInput';
import ListaLayers from '../common/listaLayers';
import ListaCampos from '../common/listaCampos';
import { ElementoListaFiltro } from '../../classes/widgetClasses';
import { getFieldTypesForSql, getFieldValueWithDomain } from '../../utils/esriUtils';
import { EstadosWidgetCerca, TipoCampo } from '../../utils/enums';
import { DatosCampo } from '../../classes/esriClasses';
import { ActionEstadosWidgetsCambiarValor, EstadosWidgets } from '../../classes/reducerClasses';

function WidgetFiltro({ zoomPuntoUnico, idLayerClear, dispatchEstadosWidgets }) {

    const { mapViewRef } = useContext(RefsContext);
    const { expandsWidgetsButtonsRef } = useContext(RefsContext);
    const { widget: widgetCerca } = expandsWidgetsButtonsRef.current.expands.cerca;
    const [idLayerSeleccionado, setIdLayerSeleccionado] = useState(null);
    const [maxRecordCountLayerSeleccionado, setMaxRecordCountLayerSeleccionado] = useState(0);
    const [datosCampos, setDatosCampos] = useState([]);
    const [nombreCampoSeleccionado, setNombreCampoSeleccionado] = useState(null);
    const [listaElementos, setListaElementos] = useState([]);
    const [filtroAplicarDefault, setFiltroAplicarDefault] = useState(true);

    const featureLayers = useRef(getFeatureLayers(mapViewRef.current.map).filter(x => !x.featureReduction));
    const tiposCamposSql = useRef(getFieldTypesForSql());

    function cambioCampo(nombreCampo){
        setNombreCampoSeleccionado(nombreCampo);
        quitarSeleccionTodos();
    }

    function handleCambioLayer(idLayer){
        removerFiltroCapa(true);
        cambioLayer(idLayer);
    }

    function cambiarSeleccionValor(index){
        const listaElementosAux = [...listaElementos];
        listaElementosAux[index].seleccionado = !listaElementosAux[index].seleccionado;
        setListaElementos(listaElementosAux);
        if (listaElementosAux.filter(x => x.seleccionado).length === 0){
            removerFiltroCapa();
        } else {
            aplicarFiltroCapa();
        }
    }

    function quitarSeleccionTodos(){
        const listaElementosAux = listaElementos.map(x => ({...x, seleccionado: false}));
        setListaElementos(listaElementosAux);
        removerFiltroCapa();
    }

    function navegarToSeleccion(){
        const layer = getLayerById(idLayerSeleccionado);
        const query = layer.createQuery();
        query.where = layer.featureEffect?.filter?.where;
        query.outSpatialReference = mapViewRef.current.map.basemap.spatialReference;
        layer.queryExtent(query).then(function(results){
            mapViewRef.current.goTo(results.extent);
            if (layer.geometryType === "point" && results.count === 1){
                mapViewRef.current.zoom = zoomPuntoUnico;
            }
        });
    }

    const getLayerById = useCallback((id) => {
        return featureLayers.current.find(x => x.id === id);
    }, []);

    const cambioLayer = useCallback((idLayer) => {
        setNombreCampoSeleccionado(null);
        setIdLayerSeleccionado(idLayer);
        fetch(`${getLayerById(idLayer).url}?f=json`).then(response => {
            response.json().then(serviceData =>
                setMaxRecordCountLayerSeleccionado(serviceData.maxRecordCount)
            );
        })
    }, [getLayerById])

    const getSeleccionados = useCallback(() => {
        return listaElementos.filter(x => x.seleccionado);
    }, [listaElementos]);

    const removerFiltroCapa = useCallback((validarSiEsDeWidgetCerca = false) => {
        const layer = getLayerById(idLayerSeleccionado);
        if (!validarSiEsDeWidgetCerca || layer.featureEffect?.includedEffect !== config.includedEffectWidgetCerca){
            layer.featureEffect = null;
        }
    }, [idLayerSeleccionado, getLayerById]);

    const aplicarFiltroCapa = useCallback(() => {
        if (idLayerSeleccionado === widgetCerca?.idLayerSeleccionado && widgetCerca.estado !== EstadosWidgetCerca.created){
            widgetCerca.estado = EstadosWidgetCerca.createdSinQuitarEfecto;
        }
        const layer = getLayerById(idLayerSeleccionado);
        const fieldSeleccionado = layer.fieldsIndex.get(nombreCampoSeleccionado);
        const valoresTexto = getSeleccionados().map(x =>
            (tiposCamposSql.current.filter(x => x.conComillas).some(y => y.tipo === fieldSeleccionado.type) ? `'${x.valor}'` : x.valor)
        ).join(",");
        layer.featureEffect = new FeatureEffect({
            filter: new FeatureFilter({
                where: `${nombreCampoSeleccionado} IN (${valoresTexto})`
            }),
            excludedEffect: "opacity(0%)"
        })
    }, [widgetCerca, idLayerSeleccionado, nombreCampoSeleccionado, getLayerById, getSeleccionados]);

    useEffect(() => {
        if (featureLayers.current.length > 0 && !idLayerSeleccionado){
            let indexLayerDefault = 0;
            for (let i = 0; i < featureLayers.current.length; i ++){
                if (getParametrosWidget(featureLayers.current[i], config.codigosWidgets.defaultFiltro)){
                    indexLayerDefault = i;
                    break;
                }
            }
            cambioLayer(featureLayers.current[indexLayerDefault].id);
        }
    }, [idLayerSeleccionado, cambioLayer]);

    useEffect(() => {
        if (idLayerSeleccionado && !nombreCampoSeleccionado){
            const layer = getLayerById(idLayerSeleccionado);
            let camposMostrar = [];
            camposMostrar = layer.fields.filter(campo => tiposCamposSql.current.map(x => x.tipo).includes(campo.type));
            let auxDatosCampos;
            if (layer.popupTemplate?.fieldInfos?.length > 0){
                let nombresCamposPopup = layer.popupTemplate.fieldInfos.filter(x => x.visible && camposMostrar.map(x => x.name).includes(x.fieldName));
                if (nombresCamposPopup.length === 0){ //Si todo el array de field infos es visible = false
                    nombresCamposPopup = layer.popupTemplate.fieldInfos.filter(x => camposMostrar.map(x => x.name).includes(x.fieldName));
                }
                auxDatosCampos = nombresCamposPopup.map(x => new DatosCampo(x.fieldName, x.label));
            } else {
                auxDatosCampos = camposMostrar.map(x => new DatosCampo(x.name, x.alias));
            }
            const configWidgetLayerSel = getParametrosWidget(layer, config.codigosWidgets.defaultFiltro);
            if (configWidgetLayerSel?.parametrosAdicionales?.length > 1){
                auxDatosCampos = auxDatosCampos.filter(x => configWidgetLayerSel.parametrosAdicionales.includes(x.name));
            }
            setDatosCampos(auxDatosCampos);
            let indexCampo = 0;
            if (configWidgetLayerSel && filtroAplicarDefault){
                setFiltroAplicarDefault(false);
                const index = auxDatosCampos.findIndex(x => x.name === configWidgetLayerSel.parametrosAdicionales[0]);
                if (index > -1){
                    indexCampo = index;
                }
            }
            setNombreCampoSeleccionado(auxDatosCampos[indexCampo].name);
        }
    }, [idLayerSeleccionado, nombreCampoSeleccionado, getLayerById, filtroAplicarDefault]);

    useEffect(() => {
        if (nombreCampoSeleccionado && (!idLayerClear || idLayerClear === idLayerSeleccionado)){
            const layer = getLayerById(idLayerSeleccionado);
            const fieldSeleccionado = layer.fieldsIndex.get(nombreCampoSeleccionado);
            const query = layer.createQuery();
            query.outFields = [nombreCampoSeleccionado];
            query.orderByFields = [nombreCampoSeleccionado];
            query.returnDistinctValues = true;
            query.returnGeometry = false;
            query.maxRecordCountFactor = 5;
            setListaElementos([]); //Muestra el aviso de generando lista mientras hace el query
            layer.queryFeatures(query).then(({ features }) => {
                const listaElementosAux = [];
                features.forEach(feature => {
                    const valorAux = feature.attributes[nombreCampoSeleccionado];
                    let labelAux = getFieldValueWithDomain(layer, nombreCampoSeleccionado, valorAux);
                    if (!labelAux){ //Campo no es coded-value
                        labelAux = valorAux;
                        if (fieldSeleccionado.type === TipoCampo.string && valorAux.trim() === ""){
                            labelAux = config.enBlancoLabel;
                        } else if (fieldSeleccionado.type === TipoCampo.date){
                            //NO se ha probado ni implementado porque no se soportan aún campos de fecha!!!!:
                            // const valorFecha = new Date(valorAux);
                            // labelAux = valorFecha.toString("d/M/yyyy");
                        }
                    }
                    listaElementosAux.push(new ElementoListaFiltro(valorAux, labelAux, false));
                });
                setListaElementos(listaElementosAux);
            })
            if (idLayerClear){
                dispatchEstadosWidgets(new ActionEstadosWidgetsCambiarValor(EstadosWidgets.propIdLayerClearEnFiltro, null));
            }
        }
    }, [idLayerClear, mapViewRef, idLayerSeleccionado, nombreCampoSeleccionado, getLayerById, dispatchEstadosWidgets]);

    if (!idLayerSeleccionado || !nombreCampoSeleccionado){
        return <span className='panelAviso panelColorAceptado'>No hay capas con acceso a registros en el mapa</span>;
    }

    const numSeleccionados = getSeleccionados().length;
    const conSeleccionStyleVisible = { display: numSeleccionados > 0 ? "block" : "none"};

    return (
        <div className='customWidget'>
            <div className='customWidgetSection'>
                <TextoInput texto="Capa"/>
                <ListaLayers 
                    idLayerSeleccionado={idLayerSeleccionado}
                    handleCambioLayer={handleCambioLayer}
                    disabled={false}
                    featureLayers={featureLayers.current}
                />
            </div>
            <div className='customWidgetSection'>
                <TextoInput texto="Campo"/>
                <ListaCampos
                    datosCampos={datosCampos}
                    nombreCampoSeleccionado={nombreCampoSeleccionado}
                    handleCambioCampo={cambioCampo}
                />
            </div>
            <div className='customWidgetSection' style={{textAlign: "center"}}>
                <span className='avisoLista'>
                    {`${numSeleccionados} valor${(numSeleccionados === 1 ? "" : "es")} seleccionado${(numSeleccionados === 1 ? "" : "s")}`}
                </span>
            </div>           
            <div className='customWidgetSection customWidgetTableContainer'>
                {
                    listaElementos.length === 0 ?
                    <div className='avisosLista blink'>
                        Generando lista...
                    </div>
                    :
                    <table className='tablaLista' width="100%" style={{overflowY: "scroll"}}>
                        <tbody>
                            {
                                listaElementos.map(({ label, seleccionado }, index) =>
                                    <tr key={index} className={ "elementoFiltro cursorPointer" + (seleccionado ? " elementoFiltroSeleccionado" : "") } title={seleccionado ? "Quitar de la selección" :"Seleccionar"}>
                                        <td onClick={() => cambiarSeleccionValor(index)}>
                                            {label}
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                }
            </div>
            <div className='customWidgetSection avisoPrecaucionSmall' style={{textAlign: "center", display: maxRecordCountLayerSeleccionado === 0 || maxRecordCountLayerSeleccionado > listaElementos.length  ? "none" : "block"}}>
                {"Es posible que hayan más valores disponibles."}
                <br/>
                {`Se pueden visualizar máximo ${maxRecordCountLayerSeleccionado}.`}
            </div> 
            <div className='customWidgetSection' style={conSeleccionStyleVisible}>
                <button className='esri-button' onClick={navegarToSeleccion}>
                    Navegar a la selección
                </button>
            </div>
            <div className='customWidgetSection' style={conSeleccionStyleVisible}>
                <button className='esri-button' onClick={quitarSeleccionTodos}>
                    Borrar la selección
                </button>
            </div>
        </div>
    );
}

export default WidgetFiltro;